import {
  useToast,
  HStack,
  IconButton,
  Box,
  Stack,
  FormLabel,
  Textarea,
  ButtonGroup,
  Button,
  Divider,
  Input,
  VStack,
  Tag,
  Text,
  TagLabel,
} from "@chakra-ui/react";
import { useRef, useEffect, useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DropdownWithSearch from "../../../../components/Inputs/DropDownSelector";
import { useAuth, Role } from "../../../../Providers/AuthProvider";
import JsonRender, { LessonClickEvent } from "../../../../renderer/JsonRender";
import { useAppSelector, useAppDispatch } from "../../../../store";
import {
  updateLessonState,
  cleanLesson,
} from "../../../../store/slices/lessonSlice";
import {
  useGenerateLesson,
  useSaveLesson,
  useGetAlterante,
  usePublishLesson,
  useDeleteLesson,
  useGetTools,
  useGetModule,
} from "../../api";
import { assets } from "../../../../assets";
import { PluginType } from "../../../../plugins/ai";

const tags = ["Begineer", "Advanced", "Intuitive"];

export const useLessonState = () => {
  const {
    currentLesson,
    alternateRequirements,
    currentSelectedContentId,
    alterantesOptions,
    rightPanel,
    allLessons,
    originalLessons,
  } = useAppSelector((s) => s.lesson);
  const [isImageDrawerOpen, setIsImageDrawerOpen] = useState(false);
  const [isPluginDrawerOpen, setIsPluginDrawerOpen] = useState(false);
  const { role } = useAuth();
  const [selectedTool, setSelectedTool] = useState("");
  const dispach = useAppDispatch();
  const generateLesson = useGenerateLesson();
  const saveLesson = useSaveLesson();
  const alternateContent = useGetAlterante();
  const publishLesson = usePublishLesson();
  const removeLesson = useDeleteLesson();
  const { courseId, moduleId } = useParams();
  const { data: toolsData } = useGetTools();
  const [dialog, setDialog] = useState<"remove" | "publish" | "plugin" | null>(
    null
  );
  const [openPluginPropsChange, setIsPluginPropsChange] = useState(false);
  const toast = useToast();
  const pasteButtonRef = useRef<any>(null);
  const modules = useGetModule(moduleId ?? "");
  const navigate = useNavigate();
  const [currentContentId, setCurrentContentId] = useState<null | number>(null);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    if (modules.data) {
      dispach(
        updateLessonState({
          originalLessons: modules.data.lessons as any,
          allLessons: modules.data.lessons as any,
        })
      );
    }
  }, [modules.data, modules.isSuccess]);

  const showToast = (status: boolean, title: string, desc?: string) =>
    toast({
      title,
      position: "top",
      description: desc,
      status: status ? "success" : "error",
      duration: 5000,
      isClosable: true,
    });

  const handleFetchLesson = useCallback(async () => {
    generateLesson.mutate(
      {
        lessonName: currentLesson?.title!,
        requirements: currentLesson?.requirements!,
        customLesson: currentLesson.customLesson,
      },
      {
        onSuccess: (res: any) => {
          console.log(res, "--lesson");
          dispach(
            updateLessonState({
              currentLesson: { ...currentLesson, content: res.lesson },
            })
          );
          showToast(true, "Genderated Successfully");
        },
        onError: (err) => {
          showToast(false, "Failed to generate Lesson");
          console.info(err, "---ERROR TO FETCH COURSE");
        },
      }
    );
  }, [currentLesson]);

  const handleMobileView = () => {
    setIsMobileView((p) => !p);
  };

  const handleAlternateContent = useCallback(async () => {
    alternateContent.mutate(
      {
        lessonName: currentLesson?.title,
        tool: selectedTool,
        requirement: alternateRequirements,
      },
      {
        onSuccess: (res) => {
          dispach(
            updateLessonState({
              alterantesOptions: res.lesson,
            })
          );
        },
        onError: (err) => {
          console.info(err, "---ERROR TO Alternate");
        },
      }
    );
  }, [currentLesson, alternateRequirements, selectedTool]);

  const handleContentClick = useCallback(
    (contentId: number, event: LessonClickEvent) => {
      setCurrentContentId(null);
      if (event === "click") {
        dispach(
          updateLessonState({
            rightPanel: "alternate",
            currentSelectedContentId: contentId,
          })
        );
      }
      if (event === "add") {
        const newContent = [...currentLesson.content];

        newContent.splice(contentId, 0, {} as IConfig);

        dispach(
          updateLessonState({
            currentLesson: { ...currentLesson, content: newContent },
          })
        );
      }
      if (event === "add-below") {
        const newContent = [...currentLesson.content];

        newContent.splice(contentId + 1, 0, {} as IConfig);

        dispach(
          updateLessonState({
            currentLesson: { ...currentLesson, content: newContent },
          })
        );
      }
      if (event === "remove") {
        const newContent = [...currentLesson.content];

        newContent.splice(contentId, 1);

        dispach(
          updateLessonState({
            currentLesson: { ...currentLesson, content: newContent },
          })
        );
      }
      if (event === "add-plugin") {
        setIsPluginDrawerOpen(true);
        setCurrentContentId(contentId);
      }

      if (event == "change-plugin-props") {
        setCurrentContentId(contentId);
        setIsPluginPropsChange(true);
      }
    },
    [currentLesson.content]
  );

  const handleAlternateSelect = useCallback(
    (alternateElemIndex: number) => {
      const alternateContent = alterantesOptions[alternateElemIndex];

      const newContentList = [...currentLesson.content];
      newContentList[currentSelectedContentId!] = alternateContent;

      dispach(
        updateLessonState({
          currentLesson: { ...currentLesson, content: newContentList },
        })
      );
    },
    [currentLesson, alterantesOptions, currentSelectedContentId]
  );

  const clearCurrentAddChange = useCallback(() => {
    const newContentList = [...currentLesson.content];
    const currentLessonIndex = originalLessons.findIndex(
      (l) => l._id === currentLesson._id
    );

    if (currentLessonIndex > 0) {
      newContentList[Number(currentSelectedContentId)] =
        originalLessons[currentLessonIndex].content[currentLessonIndex];

      dispach(
        updateLessonState({
          alterantesOptions: [],
          currentLesson: { ...currentLesson, content: newContentList },
        })
      );
    }
  }, [currentLesson, currentSelectedContentId, originalLessons]);

  const handleSaveLesson = useCallback(async () => {
    saveLesson.mutate(
      { ...currentLesson, module: moduleId! },
      {
        onSuccess: () => {
          showToast(true, "Saved successfully");
          modules.refetch();
        },
        onError: (err) => {
          console.info(err, "---ERROR TO Alternate");
          showToast(false, "Failed to save Lesson");
        },
        onSettled: () => {},
      }
    );
  }, [currentLesson]);

  const handlePublishLesson = useCallback(async () => {
    publishLesson.mutate(
      {
        _id: currentLesson._id!,
      },
      {
        onSuccess: () => {
          showToast(true, "published successfully");
          modules.refetch();
        },
        onError: (err) => {
          console.info(err, "---ERROR TO Alternate");
          showToast(false, "Failed to publish Lesson");
        },
      }
    );
  }, [currentLesson]);

  useEffect(() => {
    return () => {
      dispach(cleanLesson());
    };
  }, []);

  const handleLessonRemove = useCallback(() => {
    removeLesson.mutate(currentLesson._id, {
      onSuccess: () => {
        showToast(true, "Lesson removed successfully");
        modules.refetch();
      },
      onError: (err) => {
        console.info(err, "---ERROR TO Alternate");
        showToast(false, "Failed to Remove Lesson");
      },
      onSettled: () => {
        setDialog(null);
      },
    });
  }, [currentLesson._id]);

  useEffect(() => {
    const checkClipboard = async () => {
      try {
        if (!pasteButtonRef.current) return;
        const text = await navigator.clipboard.readText();
        if (text.trim().length > 0) {
          pasteButtonRef.current.style.display = "inline-block";
        } else {
          pasteButtonRef.current.style.display = "none";
        }
      } catch (err) {
        console.error("Clipboard read error:", err);
      }
    };

    const interval = setInterval(checkClipboard, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleCopyLesson = async () => {
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(currentLesson, null, 2)
      );
      showToast(true, "Copied to clipboard!");
      console.log("Copied to clipboard!");
    } catch (err) {
      showToast(false, "Failed to copy");
    }
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      dispach(
        updateLessonState({
          currentLesson: JSON.parse(text),
        })
      );
    } catch (err) {
      console.error("Failed to paste:", err);
    } finally {
      await navigator.clipboard.writeText("");
    }
  };

  const handlePluginSelect = (p: PluginType) => {
    if (!currentContentId) return;

    const newContent = [...currentLesson.content];

    newContent.splice(currentContentId, 0, {
      component: "PluginComponent",
      props: { type: p.name, props: p.defaultProps },
    } as IConfig);

    dispach(
      updateLessonState({
        currentLesson: { ...currentLesson, content: newContent },
      })
    );

    setIsPluginDrawerOpen(false);
  };

  const mainPanel = (
    <Box>
      <ButtonGroup mb={"1rem"}>
        <Button
          variant={"teronary"}
          isLoading={generateLesson.isPending}
          onClick={handleFetchLesson}
        >
          Generate new lesson
        </Button>
        {currentLesson.content.length > 0 && (
          <>
            <Button
              variant={"solid"}
              onClick={handleSaveLesson}
              colorScheme="blue"
              isLoading={saveLesson.isPending}
            >
              save Lesson
            </Button>
            {(role === Role.SuperAdmin || role === Role.Admin) &&
              currentLesson._id && (
                <Button
                  onClick={handlePublishLesson}
                  variant={"solid"}
                  colorScheme="green"
                  isLoading={publishLesson.isPending}
                >
                  publish
                </Button>
              )}
          </>
        )}
      </ButtonGroup>
      <Box>
        <Button
          my={"1rem"}
          ref={pasteButtonRef}
          variant="outline"
          w="full"
          onClick={pasteFromClipboard}
        >
          Paste lesson here
        </Button>

        <FormLabel>Lesson Namae with description</FormLabel>
        <Input
          value={currentLesson.title}
          onChange={(e) =>
            dispach(
              updateLessonState({
                currentLesson: { ...currentLesson, title: e.target.value },
              })
            )
          }
          placeholder="Lesson Name"
          type="text"
          mb={"1rem"}
        ></Input>
        <FormLabel>Custom Lesson text</FormLabel>
        <Textarea
          value={currentLesson.customLesson}
          onChange={(e) => {
            dispach(
              updateLessonState({
                currentLesson: {
                  ...currentLesson,
                  customLesson: e.target.value,
                },
              })
            );
          }}
          rows={25}
          mb={"1rem"}
          placeholder="Custom Lesson here"
        ></Textarea>
        {/* <FormLabel>requirement</FormLabel>
                  <Textarea
                    value={currentLesson.requirements[0]}
                    onChange={(e) => {
                      dispach(
                        updateLessonState({
                          currentLesson: {
                            ...currentLesson,
                            requirements: [e.target.value, currentLesson.requirements[1]],
                          },
                        })
                      );
                    }}
                    rows={10}
                    mb={"1rem"}
                    placeholder="Lesson requirement"
                  ></Textarea> */}
        {/* <FormLabel>Part 2 requirement</FormLabel>
                  <Textarea
                    value={currentLesson.requirements[1]}
                    onChange={(e) => {
                      dispach(
                        updateLessonState({
                          currentLesson: {
                            ...currentLesson,
                            requirements: [currentLesson.requirements[0], e.target.value],
                          },
                        })
                      );
                    }}
                    rows={10}
                    mb={"1rem"}
                    placeholder="Lesson requirement"
                  ></Textarea> */}
        <FormLabel>Select the Type</FormLabel>
        <VStack gap={"1rem"}>
          <HStack spacing={4}>
            {tags.map((tag: string) => (
              <Tag
                cursor={"pointer"}
                onClick={() => {
                  dispach(
                    updateLessonState({
                      currentLesson: { ...currentLesson, type: tag },
                    })
                  );
                }}
                size={"lg"}
                key={tag}
                variant={tag === currentLesson.type ? "solid" : "outline"}
                colorScheme={tag === currentLesson.type ? "blue" : "neutral"}
                borderRadius="full"
              >
                <TagLabel>{tag}</TagLabel>
              </Tag>
            ))}
          </HStack>
          <Button w={"full"} variant={"outline"} onClick={handleCopyLesson}>
            Copy Lesson
          </Button>
        </VStack>
      </Box>
      {/* <Button
              onClick={() => setDrawerOpen(false)}
              m={"1rem"}
              ms={"auto"}
              width={"fit-content"}
              variant={"secondary"}
            >
              Save
            </Button> */}
    </Box>
  );

  const alternatePannel = (
    <>
      <HStack mb={"1rem"} alignItems={"center"}>
        <IconButton
          aria-label="close lesson"
          border={"1px solid white"}
          borderRadius={".9rem"}
          variant={"outline"}
          size={"md"}
          onClick={() => dispach(updateLessonState({ rightPanel: "main" }))}
          icon={assets.iconComponents.backIcon()}
        ></IconButton>
        <Text fontSize={"lg"} fontWeight={"bold"}>
          Change Content
        </Text>
      </HStack>
      <Box>
        <Stack>
          <FormLabel>Tool Detail</FormLabel>
          <Textarea
            rows={15}
            onClick={() => {}}
            value={alternateRequirements}
            onChange={(e) =>
              dispach(
                updateLessonState({
                  alternateRequirements: e.target.value,
                })
              )
            }
            placeholder="Details of what you need as alternative"
          ></Textarea>
          <FormLabel>Tool</FormLabel>
          <Box mb={"1rem"}>
            <DropdownWithSearch
              options={(toolsData || [])?.map((tool, i) => ({
                id: i,
                label: tool,
              }))}
              selectedOption={selectedTool}
              placeHolder="Select a tool"
              onChange={(option) => {
                setSelectedTool(option);
              }}
            />
          </Box>
          <ButtonGroup>
            <Button
              onClick={handleAlternateContent}
              isLoading={alternateContent.isPending}
            >
              Generate
            </Button>
            <Button variant={"outline"} onClick={clearCurrentAddChange}>
              Clear
            </Button>
          </ButtonGroup>
          <Divider bg={"whiteAlpha.400"} my={"1rem"} />
          <Box sx={{ zoom: "0.8" }}>
            <JsonRender
              width="100%"
              lessonId=""
              mode="selection"
              onClick={handleAlternateSelect}
              content={alterantesOptions}
            />
          </Box>
        </Stack>
      </Box>
    </>
  );

  return {
    dispach,
    state: {
      isImageDrawerOpen,
      isPluginDrawerOpen,
      role,
      selectedTool,
      generateLesson,
      saveLesson,
      alternateContent,
      publishLesson,
      removeLesson,
      courseId,
      toolsData,
      dialog,
      rightPanel,
      allLessons,
      currentContentId,
      modules,
      currentLesson,
      isMobileView,
      openPluginPropsChange,
    },
    handlers: {
      handleLessonRemove,
      setDialog,
      dispach,
      navigate,
      handleCopyLesson,
      pasteFromClipboard,
      handlePluginSelect,
      handleSaveLesson,
      handleAlternateContent,
      handleAlternateSelect,
      handleContentClick,
      handleFetchLesson,
      clearCurrentAddChange,
      handlePublishLesson,
      setIsImageDrawerOpen,
      setIsPluginDrawerOpen,
      handleMobileView,
      setIsPluginPropsChange,
    },
    panels: {
      alternatePannel,
      mainPanel,
    },
  };
};
